import { EventTracker } from '@/utils';
import { utcToLocal } from '@/utils/date/dateUtils';
import { cn } from '@/utils/styles';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
} from '@design-system/Sheet';
import { useGetTrackV3 } from '@viome/viome-base/src/usecase/Home/HomeV2Query';
import { ChevronRight, X } from 'lucide-react';
import { StepProgressBar } from './StepProgressBar';
import { TrackingSampleSkeleton } from './TrackingSampleSkeleton';

interface TrackingSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLearnMore: () => void;
}

export const TrackingSampleModalV2 = ({
  isOpen,
  onClose,
  onLearnMore,
}: TrackingSampleModalProps) => {
  const { data: testStatus, isLoading, isError } = useGetTrackV3();

  if (!isOpen) return null;

  if (isLoading) {
    return <TrackingSampleSkeleton isOpen={isOpen} onClose={onClose} />;
  }

  const sheetChrome = (children: React.ReactNode) => (
    <Sheet
      open={isOpen}
      onOpenChange={onClose}
      data-testid="trackingSample-modal"
    >
      <SheetPortal>
        <SheetOverlay data-testid="trackingSample-overlay" />
        <SheetContent
          side="right"
          className={cn(
            'pr-3 pl-6',
            'bg-background-secondary',
            'shadow-[0px_1px_0px_0px_#FFFFFF1A_inset,0px_4px_40px_0px_#44445E29]',
          )}
          variant="glass"
          hideCloseButton
          data-testid="trackingSample-content"
        >
          <SheetHeader
            className="mb-0 pt-6 pl-1 mt-3"
            data-testid="trackingSample-header"
          >
            <div className="flex items-center justify-between">
              <h2
                className="typography-h2 text-primary font-semibold"
                data-testid="trackingSample-title"
              >
                Test Status
              </h2>
              <button
                onClick={onClose}
                className="text-static-secondary hover:text-static-primary transition-colors"
                data-testid="trackingSample-closeButton"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </SheetHeader>
          {children}
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );

  if (isError || !testStatus) {
    return sheetChrome(
      <div
        className="flex items-center justify-center h-40"
        data-testid="trackingSample-error"
      >
        <p className="typography-body text-secondary">
          Something went wrong. Please try again later.
        </p>
      </div>,
    );
  }

  const currentStepIndex = testStatus.track_status_id - 1;
  const issueInCurrentStatus = testStatus.is_issue_in_current_status;
  const localTime = utcToLocal(testStatus.updated_at);

  return (
    <>
      {sheetChrome(
        <div
          className="relative px-1 pb-8 flex flex-col gap-6 mt-4"
          data-testid="trackingSample-body"
        >
          {localTime && (
            <p
              className="text-xs text-secondary"
              data-testid="trackingSample-lastUpdated"
            >
              Last updated on {localTime}
            </p>
          )}
          <div className="flex flex-col gap-1">
            <h2
              className="typography-h2 font-bold text-primary"
              data-testid="trackingSample-statusHeader"
            >
              {testStatus.status_header}
            </h2>
            <p
              className="typography-body text-secondary mt-1"
              data-testid="trackingSample-statusSubheader"
            >
              {testStatus.status_subheader}
            </p>
          </div>

          <StepProgressBar
            steps={testStatus?.track_status_list ?? []}
            currentStepIndex={currentStepIndex}
            hasError={issueInCurrentStatus}
          />

          {testStatus.eta_display_text && !issueInCurrentStatus && (
            <div
              data-testid="trackingSample-eta"
              className="rounded-2xl px-5 py-4 flex flex-col gap-1.5 bg-background"
            >
              <span className="text-xs font-semibold tracking-wide text-secondary">
                ESTIMATED RESULTS
              </span>
              <span
                className={cn(
                  'typography-h3 font-semibold',
                  testStatus.is_eta_over_ceiling
                    ? 'text-amber-500'
                    : 'text-primary',
                )}
              >
                {testStatus.eta_display_text}
              </span>
            </div>
          )}

          {testStatus.lab_process && (
            <button
              data-testid="trackingSample-labProcess"
              className="flex items-center gap-1 text-tertiary"
              onClick={() => {
                EventTracker.trackEvent(
                  'home',
                  'sample_tracker_learn_more_tap',
                );
                onLearnMore();
              }}
            >
              <span className="typography-body text-sm underline">
                Learn about the lab process
              </span>
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>,
      )}
    </>
  );
};
